import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "@/i18n"
import { Icon } from "@/components/Icon"
import {
  defaultPostFilters,
  resetCategory as resetCategoryOf,
  type ActiveFilter,
  type FilterCategory,
  type PostFilterState,
} from "@/nostr/api/filters/filterEngine"
import { ActiveFilterChips } from "./ActiveFilterChips"
import { ContentFilters } from "./ContentFilters"
import { FilterDrawer } from "./FilterDrawer"
import { PeopleSourceFilters } from "./PeopleSourceFilters"
import { TimeEngagementFilters } from "./TimeEngagementFilters"

export interface PostFiltersProps {
  /** Applied state — owned by `usePostFilters`, never duplicated here. */
  value: PostFilterState
  active: ActiveFilter[]
  /** Commits a complete state (the drawer's draft). */
  onApply: (next: PostFilterState) => void
  /** Resets every section immediately (chip bar). */
  onClearAll: () => void
  /** Removes one chip immediately. */
  onRemove: (key: string) => void
  /** Sections to show; pages only display the filters that make sense for them. */
  categories?: FilterCategory[]
  /** Relays of this list (source filter). */
  sources?: string[]
  /** The reader's follow list (quick author picks). */
  following?: string[]
  showSources?: boolean
  showFollowing?: boolean
  /** Hide the post type control (pages whose tabs select the post type). */
  hidePostType?: boolean
  mutedWordCount?: number
  className?: string
}

/**
 * The unified filter control: a compact button with the number of active
 * filters, the active chips, and a drawer that groups the three categories.
 *
 * The drawer edits a **draft**; nothing is applied until Apply is pressed and
 * Cancel restores the current state, which keeps expensive filters (engagement,
 * language, media) from re-running on every keystroke.
 */
export function PostFilters({
  value,
  active,
  onApply,
  onClearAll,
  onRemove,
  categories = ["content", "time", "people"],
  sources = [],
  following = [],
  showSources = false,
  showFollowing = true,
  hidePostType = false,
  mutedWordCount = 0,
  className = "",
}: PostFiltersProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<PostFilterState>(value)

  // Re-sync when the applied state changes outside the drawer (chips, page reset).
  useEffect(() => {
    if (!open) setDraft(value)
  }, [value, open])

  const patchDraft = useCallback((patch: Partial<PostFilterState>) => {
    setDraft((current) => ({ ...current, ...patch }))
  }, [])

  const openDrawer = () => {
    setDraft(value)
    setOpen(true)
  }

  const apply = () => {
    onApply(draft)
    setOpen(false)
  }

  const clearDraft = () => setDraft(defaultPostFilters())

  const showContent = categories.includes("content")
  const showTime = categories.includes("time")
  const showPeople = categories.includes("people")

  return (
    <div className={`post-filters ${className}`.trim()}>
      <div className="post-filters-bar">
        <button
          type="button"
          className={`btn btn-ghost btn-small filter-button ${active.length > 0 ? "active" : ""}`.trim()}
          onClick={openDrawer}
          aria-haspopup="dialog"
          aria-expanded={open}
          title={t("Filter posts")}
        >
          <Icon name="filter" size={15} />
          {t("Filters")}
          {active.length > 0 && <span className="filter-count">{active.length}</span>}
        </button>
        <ActiveFilterChips filters={active} onRemove={onRemove} onClearAll={onClearAll} />
      </div>

      <FilterDrawer
        open={open}
        title={t("Filter posts")}
        description={t("Narrow this list by content, time, engagement, people and relays.")}
        activeCount={active.length}
        onApply={apply}
        onCancel={() => setOpen(false)}
        onClearAll={clearDraft}
      >
        {showContent && (
          <ContentFilters
            value={draft}
            onChange={patchDraft}
            onReset={() => setDraft(resetCategoryOf(draft, "content"))}
            mutedWordCount={mutedWordCount}
            hidePostType={hidePostType}
          />
        )}
        {showTime && (
          <TimeEngagementFilters
            value={draft}
            onChange={patchDraft}
            onReset={() => setDraft(resetCategoryOf(draft, "time"))}
            engagementNote={t(
              "Interaction counts come from the events the connected relays delivered, so they can be lower than the real totals.",
            )}
          />
        )}
        {showPeople && (
          <PeopleSourceFilters
            value={draft}
            onChange={patchDraft}
            onReset={() => setDraft(resetCategoryOf(draft, "people"))}
            following={following}
            sources={sources}
            showFollowing={showFollowing}
            showSources={showSources}
          />
        )}
      </FilterDrawer>
    </div>
  )
}
